import type { DocType, LegalDocumentStatus, UserRole } from "@/types";

/**
 * What a row in the legal file says, and why.
 *
 * Pure, and separate from the table, because the interesting case is not how it
 * looks. It is the difference between *never accepted* and *accepted, then the
 * text changed*: both stop an owner at the door now, but for different reasons
 * and with different words, and the dashboard banner still owns everything
 * that is required without standing at the door.
 */
export type LegalRowStatus = "accepted" | "newVersion" | "never" | "optional";

export function rowStatus(document: LegalDocumentStatus): LegalRowStatus {
  if (document.accepted) return "accepted";
  if (!document.required) return "optional";
  if (document.acceptedVersion !== undefined) return "newVersion";
  return "never";
}

export const ROW_STATUS_KEY: Record<LegalRowStatus, string> = {
  accepted: "legal.status.accepted",
  newVersion: "legal.status.newVersion",
  never: "legal.status.never",
  optional: "legal.status.optional",
};

export const ROW_STATUS_TONE: Record<LegalRowStatus, string> = {
  accepted: "text-positive",
  newVersion: "text-warning",
  never: "text-danger",
  optional: "text-ink-muted",
};

/**
 * The two documents that stand at the door.
 *
 * An owner signing in for the first time reads and accepts the terms and the
 * privacy notice before seeing anything else of Backstage (founder decision,
 * 2026-09-09). The rest of the file — a data processing agreement, the
 * child-readable notice — is still required before a lesson can start, and the
 * dashboard says so; it is not what stops somebody at the door.
 *
 * A type rather than a document id: the registry publishes several documents
 * of type `privacy` (the sub-processor list, the security page), and only the
 * required one is the notice an institution accepts.
 */
export const DOOR_TYPES: readonly DocType[] = ["tos", "privacy"];

/** Required, never accepted, and one of the two that stand at the door. */
export function firstRunDocuments(
  documents: readonly LegalDocumentStatus[]
): LegalDocumentStatus[] {
  return documents.filter(
    (document) => DOOR_TYPES.includes(document.type) && rowStatus(document) === "never"
  );
}

/** The documents whose text changed under an owner, as opposed to nagging them. */
export function blockingDocuments(
  documents: readonly LegalDocumentStatus[]
): LegalDocumentStatus[] {
  return documents.filter((document) => rowStatus(document) === "newVersion");
}

/**
 * Who is stopped at the door, and by what.
 *
 *  - **An owner** is stopped by a door document never accepted, and by any
 *    required document whose text changed since they accepted it. The two are
 *    listed together, in the registry's order, because the answer to both is
 *    the same: read it, accept it.
 *  - **A teacher** is stopped only while the owner has never accepted the door
 *    documents. They cannot accept anything themselves, so the screen they see
 *    says whose move it is. A changed version does not stop them: blocking a
 *    teacher on a re-signature would make an owner's inbox the critical path
 *    for somebody else's lesson.
 *  - Anyone else — a player, a Spontar admin — has nothing to accept.
 */
export type GateDecision =
  | { kind: "open" }
  | { kind: "owner"; documents: LegalDocumentStatus[] }
  | { kind: "teacher"; documents: LegalDocumentStatus[] };

export function gateDecision(
  role: UserRole | undefined,
  documents: readonly LegalDocumentStatus[]
): GateDecision {
  if (role === "owner") {
    const stopping = new Set(
      [...firstRunDocuments(documents), ...blockingDocuments(documents)].map((entry) => entry.id)
    );
    const stopped = documents.filter((document) => stopping.has(document.id));
    return stopped.length === 0 ? { kind: "open" } : { kind: "owner", documents: stopped };
  }

  if (role === "teacher") {
    const waiting = firstRunDocuments(documents);
    return waiting.length === 0 ? { kind: "open" } : { kind: "teacher", documents: waiting };
  }

  return { kind: "open" };
}
